/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, Optional, signal} from '@angular/core';
import {ControlContainer} from '@angular/forms';
import * as _ from 'lodash';

import template from './asset.component.html';

import {MediaTypeHelper} from '../../../components/util/media-type-helper';
import {toUploadableFiles, UploadableFile} from '../../common/directives/file-upload/file-upload.util';
import {AssetService} from './asset.service';
import {setFormValidity} from '../../common/controller/form-validity';
import {CdnUploadApiService} from '../../admin/cdn-upload/cdn-upload-api.service';
import {notifySignalView} from '../../common/util/template-state';

const FILE_UPLOAD_LIMIT = 1;

@Component({
  standalone: false,
  selector: 'adn-asset',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetComponent implements OnInit {
  @Input() creative: any;
  @Input() paramName = 'assets';
  @Input() eventHook: any;
  @Input() type: string;
  @Input() readOnly: any;

  parentObject: any;
  accept = '.webp,.jpg,.png,.jpeg,.gif,.zip,.swf,.html,.htm,.js,.txt,.json,.css,.xhtml,.xml,.woff,.eot,.svg,.ttf,.woff,.woff2,.htc,.mp4,.mp3,.webm,.vtt,.srt';
  maxFiles: number;
  isReadOnly = false;
  allFilesProcessed = true;
  allFilesProgress = 0;
  validationWarnings: any[] = [];
  filesFailedUpload: UploadableFile[] = [];
  isDragOver = false;
  readonly viewRevision = signal(0);

  private uploadMethod: (parentId: string, data: any, onProgress?: (event: any) => void) => any;
  private acceptableMimeTypeMethod: (file: any) => boolean = () => true;
  private fileUploadLimit: number = FILE_UPLOAD_LIMIT;

  constructor(
    private readonly cdnUploadApi: CdnUploadApiService,
    @Inject(AssetService) private readonly Asset: any,
    @Optional() @Inject(ControlContainer) private readonly parentForm: ControlContainer
  ) {
  }

  ngOnInit(): void {
    this.paramName = this.paramName || 'assets';
    this.parentObject = this.creative;
    this.isReadOnly = this.readOnly === true || this.readOnly === "true";

    if (!this.parentObject) {
      return;
    }

    this.parentObject[this.paramName] = this.parentObject[this.paramName] || [];
    if (!_.isArray(this.parentObject[this.paramName])) {
      this.parentObject[this.paramName] = [this.parentObject[this.paramName]];
    }
    this.assessAssets();

    this.uploadMethod = (assetId, data, onProgress) => {
      return this.cdnUploadApi.upload(assetId, data.file, onProgress);
    };
    if (this.type === 'BULK_THIRD_PARTY') {
      this.uploadMethod = this.Asset.bulkThirdPartyUpload;
      this.accept = '.xls,.xlsx,.csv,.txt';
      this.maxFiles = 1;
    } else if (this.type === 'BULK_IMAGE') {
      this.uploadMethod = this.Asset.bulkUpload;
      this.accept = '.jpg,.png,.jpeg,.gif';
      this.maxFiles = 50;
      this.acceptableMimeTypeMethod = (file) => {
        return _.get(MediaTypeHelper.getMediaType(file.type), 'image') || false;
      };
    } else if (this.type === 'BULK_VIDEO') {
      this.uploadMethod = this.Asset.bulkUpload;
      this.accept = '.mp4,.webm,.mp3,.quicktime,.hls,.dash,.mov';
      this.maxFiles = 50;
      this.acceptableMimeTypeMethod = (file) => {
        return _.get(MediaTypeHelper.getMediaType(file.type), 'video') || false;
      };
    } else if (this.type === 'BULK_HTML') {
      this.uploadMethod = this.Asset.bulkUpload;
      this.accept = '.zip';
      this.maxFiles = 50;
      this.acceptableMimeTypeMethod = (file) => {
        return _.get(MediaTypeHelper.getMediaType(file.type), 'zip') || false;
      };
    }

    this.fileUploadLimit = this.maxFiles || FILE_UPLOAD_LIMIT;
  }

  get allowsMultiple(): boolean {
    return this.maxFiles > 1;
  }

  get dropTranslationKey(): string {
    return this.maxFiles === 1 ? 'button.upload.asset.dropSingle' : 'button.upload.asset.drop';
  }

  visibleAssets(): any[] {
    const assets = _.filter(this.parentObject[this.paramName], (asset) => {
      return asset.objectState !== 'HIDDEN' && asset.hideMe !== true;
    });
    return this.maxFiles ? _.take(assets, this.maxFiles) : assets;
  }

  showsAssetGrid(): boolean {
    return this.type !== 'BULK_THIRD_PARTY' && this.type !== 'BULK_VIDEO' && this.type !== 'BULK_IMAGE' && this.type !== 'BULK_HTML';
  }

  openFilePicker(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.upload(toUploadableFiles(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    this.upload(toUploadableFiles(event.dataTransfer?.files));
  }

  private assessAssets(): void {
    if (this.parentObject[this.paramName].length < 1) {
      if (this.parentObject.creativeFileName) {
        this.parentObject[this.paramName].push(_.pick(this.parentObject, ['creativeFileName', 'creativeWidth', 'creativeHeight', 'creativeFileSizeBytes', 'creativeMimeType', 'creativeCdnId']));
      }
      if (this.parentObject.fileName) {
        this.parentObject[this.paramName].push(_.pick(this.parentObject, ['fileName', 'width', 'height', 'fileSizeBytes', 'mimeType', 'cdnId']));
      }
    }
  }

  private updateWarnings(paramWarnings?: any): void {
    if (paramWarnings) {
      this.validationWarnings = this.validationWarnings.concat(_.isArray(paramWarnings) ? paramWarnings : [paramWarnings]);
    } else {
      this.validationWarnings = [];
    }
  }

  private upload(allFiles: UploadableFile[]): void {
    this.filesFailedUpload = [];
    const incorrectMimeType = [];
    this.updateWarnings();
    if (!allFiles || !_.isArray(allFiles) || allFiles.length < 1) {
      setFormValidity(this.parentForm, 'upload', false);
      return;
    }

    let files = _.clone(_.slice(allFiles, 0, this.fileUploadLimit));
    if (allFiles.length > this.fileUploadLimit) {
      this.updateWarnings({
        text: 'Only {{fileUploadLimit}} files can be uploaded at a time. The last {{restFilesCount}} of the {{allFilesCount}} selected files were not uploaded.',
        parameters: {
          fileUploadLimit: this.fileUploadLimit,
          restFilesCount: allFiles.length - this.fileUploadLimit,
          allFilesCount: allFiles.length
        },
        preventsAdServing: false
      });
    }
    files = _.filter(files, (f) => {
      const mimeTypeResult = this.acceptableMimeTypeMethod(f);
      if (!mimeTypeResult) {
        this.updateWarnings({
          text: 'The file named {{fileName}} was not uploaded because it is not the correct type.',
          parameters: {
            fileName: f.name
          },
          preventsAdServing: false
        });
        incorrectMimeType.push(f);
      }
      return mimeTypeResult;
    });

    const uploadStats = {
      allFilesCount: allFiles.length,
      fileUploadLimit: this.fileUploadLimit,
      filesCount: files.length,
      filesFailedUpload: this.filesFailedUpload,
      incorrectMimeType: incorrectMimeType,
      apiResponseCount: 0
    };

    allFiles = null;

    if (files.length < 1) {
      return;
    }

    // add a pending validation
    setFormValidity(this.parentForm, 'upload');
    this.allFilesProcessed = false;
    this.allFilesProgress = 0;
    notifySignalView(this.viewRevision);

    const setAllFilesProgress = () => {
      const progress = _.reduce(files, (result, file) => {
        return result + (_.isFinite(file.progress) ? file.progress : 0);
      }, 0);
      this.allFilesProgress = Math.min(100, parseInt(String(progress / files.length), 10));
      notifySignalView(this.viewRevision);
    };
    const allFilesChecks = () => {
      if (!_.find(files, ['processed', false])) {
        this.allFilesProcessed = true;
      }

      // if at least one file is valid, then say the upload is a success
      setFormValidity(this.parentForm, 'upload', _.some(files, 'validity'));

      this.filesFailedUpload = _.filter(files, ['validity', false]);
      notifySignalView(this.viewRevision);
    };

    _.forEach(files, (file) => {
      file.processed = false;
      file.validity = undefined;

      const errorMethod = (error) => {
        file.processed = true;
        file.validity = false;

        let warnings = [];
        if (!error.data || (error.status === -1 && error.data === null)) {
          if (this.type === 'BULK_THIRD_PARTY' || this.type === 'BULK_IMAGE' || this.type === 'BULK_VIDEO') {
            warnings.push({
              text: 'We could not extract any third-party creative details from the document that was uploaded.',
              preventsAdServing: false
            });
          } else {
            // sometimes the API can crap out and issue a time-out, which is handled here.
            warnings.push({
              text: "The upload failed to complete. Try uploading again or, if possible, refreshing the page to see which resources have been uploaded.",
              preventsAdServing: false
            });
          }
        } else {
          // API can return validation error or general error.
          // General error on unsupported mime type, validation error on everything else (like file size too big).
          if (error.data.errors) {
            warnings = _.flatten(warnings.concat(_.map(error.data.errors, (value: any) => {
              const returnValue = _.cloneDeep(value.error);
              returnValue.preventsAdServing = true;
              return returnValue;
            })));
          } else if (error.data.map) {
            warnings = _.flatten(warnings.concat(_.map(error.data.map, (value: any) => {
              return {text: value, preventsAdServing: true};
            })));
          } else if (error.data) {
            warnings.push(error.data);
          }
        }

        uploadStats.apiResponseCount++;
        this.updateWarnings(warnings);
        allFilesChecks();
      };

      const progressMethod = (progressEvent) => {
        file.progress = Math.min(100, parseInt(String(100 * progressEvent.loaded / progressEvent.total), 10));
        setAllFilesProgress();
      };

      this.uploadMethod(this.parentObject.id, {file: file}, progressMethod).then((parentObject) => {
        file.processed = true;
        file.validity = true;

        if (parentObject[this.paramName]) {
          this.parentObject[this.paramName] = _.isArray(parentObject[this.paramName]) ? [_.last(parentObject[this.paramName])] : [parentObject[this.paramName]];
          this.isReadOnly = true;
        } else {
          this.parentObject[this.paramName] = _.isArray(parentObject) ? parentObject : [parentObject];
        }

        allFilesChecks();
        uploadStats.apiResponseCount++;
        if (_.isFunction(_.get(this.eventHook, 'onUpdate'))) {
          this.eventHook.onUpdate(parentObject, this.allFilesProcessed, uploadStats);
        }
        this.assessAssets();
      }, errorMethod);
    });
  }

}
